import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Package, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MembershipStatus, SprintEntity, useListProductManagers } from '@data';
import { useListPmMembers } from '@data/hooks/membership';
import { getDaysArray } from '@data/api/utils/helpers';
import { dayKey, fullDate, useOrganizationId } from '@app/utils';
import { shortfallOf } from '@app/utils/helpers/shortfalls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';

interface CloseProductConfirmProps {
  productId: string;
  productName: string;
  sprint: SprintEntity;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Gate for closing a product sprint. Before closing we check whether everyone on the product's team who still
 * works here has filed the time the sprint asks of them: eight hours for every working day of it up to today,
 * so eighty over a full two weeks. If they all have, we close straight away (this renders nothing and fires
 * onConfirm). If some are short, we surface a warning naming them and the hours they still owe — closing locks
 * the sprint, so those people would lose the chance to report their remaining time.
 *
 * The hours are counted across every product, not only this one. People work on several products at a time, so
 * a day with nothing on this one is usually a day that went to another — asking this product for a report on
 * every day named everybody with a second product and made the warning meaningless. What a close actually takes
 * away is the chance to file time that has been filed nowhere, and that is what a total short of the sprint's
 * hours says.
 */
const CloseProductConfirm = ({
  productId,
  productName,
  sprint,
  onCancel,
  onConfirm,
}: CloseProductConfirmProps): ReactElement | null => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();

  const { data: managerData, isLoading: managersLoading } = useListProductManagers(productId);

  // The product's team. On the prototype this was the involved people plus a per-product list kept in
  // browser storage; epic 3 made the team real, and this endpoint already answers with everyone involved
  // plus everyone tagged as managing it, so the stored list has nothing left to add.
  //
  // Deactivated people are left out: they cannot sign in, so closing the sprint takes no chance away from
  // them, and the days they never reported are days nobody is going to report. Listing them made the
  // warning permanent — everyone who ever left the company sat in it, and no amount of reporting by the
  // people still here could clear it.
  const people = useMemo(
    () =>
      (managerData?.items ?? [])
        .filter(c => c.status !== MembershipStatus.deactivated)
        .map(c => ({ id: c.membershipId, name: `${c.firstName} ${c.lastName}` })),
    [managerData],
  );

  // The stretch this close has to account for: the sprint's working days up to today, since nobody can report
  // for a day that hasn't happened yet, and the window to ask for the team's time over.
  //
  // The window closes at the end of its last day rather than at that day's midnight: `workday` is a timestamp
  // and a report carries the clock time it was filed at, so a midnight bound drops the last day's reports —
  // and the last day of a sprint is the day a close happens on.
  const { workingDays, timeWindow } = useMemo(() => {
    const today = dayKey(new Date());
    const start = fullDate(sprint.startDate);
    const end = fullDate(sprint.endDate) < today ? fullDate(sprint.endDate) : today;

    return {
      workingDays: getDaysArray(true, start, end),
      timeWindow: {
        from: `${start}T00:00:00.000Z`,
        to: `${end}T23:59:59.999Z`,
        userStatus: MembershipStatus.active,
      },
    };
  }, [sprint]);

  // Everyone's filed time over that window, whichever product it went to. Asked for org-wide because the
  // question is org-wide: this is the same read the Insights tab makes to say who owes hours over a sprint.
  const { data: membersData, isLoading: membersLoading } = useListPmMembers(
    organizationId,
    timeWindow,
    !!workingDays.length,
  );

  const ready = !managersLoading && !membersLoading;

  // Per person, the minutes they filed over the window and the day they joined. The join day is here because
  // the days before it are not days they owe — a new hire's first sprint would otherwise be a debt they cannot
  // pay, which is the warning that fires forever.
  const filedByPerson = useMemo(
    () =>
      new Map(
        (membersData?.items ?? []).map(member => [
          member.id,
          { minutes: member.totalMinutes ?? 0, joined: fullDate(member.createdAt) },
        ]),
      ),
    [membersData],
  );

  const missing = useMemo(
    () =>
      people
        .map(person => {
          const filed = filedByPerson.get(person.id);
          const owedDays = filed ? workingDays.filter(day => day >= filed.joined).length : workingDays.length;

          return { ...person, missingHours: shortfallOf(filed?.minutes ?? 0, owedDays) };
        })
        .filter(person => person.missingHours > 0)
        .sort((a, b) => b.missingHours - a.missingHours || a.name.localeCompare(b.name)),
    [people, filedByPerson, workingDays],
  );

  // Everyone filed their hours (or there's nobody/nothing to check) — close without bothering the user. Guarded so
  // the mutation fires exactly once even as the parent re-renders around the unmount.
  const fired = useRef(false);
  useEffect(() => {
    if (ready && missing.length === 0 && !fired.current) {
      fired.current = true;
      onConfirm();
    }
  }, [ready, missing.length, onConfirm]);

  // Bottom fade on the (scrollable) people list — hints there's more when the list overflows, and hides
  // once you've scrolled to the end or it doesn't overflow at all. A callback ref measures the moment the
  // list mounts (layout is available then); onScroll keeps it in sync as the user scrolls.
  const [atBottom, setAtBottom] = useState(true);
  const measureFade = useCallback((el: HTMLUListElement | null): void => {
    if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 8);
  }, []);

  if (!ready || missing.length === 0) return null;

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="size-5 shrink-0 text-amber-500" />
            {t('closeProductConfirm.title', { product: productName })}
          </DialogTitle>
          <DialogDescription>{t('closeProductConfirm.intro', { product: productName })}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <ul
            ref={measureFade}
            onScroll={e => measureFade(e.currentTarget)}
            className="max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border"
          >
            {missing.map(person => (
              <li key={person.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="font-medium text-foreground">{person.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t('closeProductConfirm.missingHours', { count: person.missingHours })}
                </span>
              </li>
            ))}
          </ul>
          {/* Bottom fade — signals there's more to scroll; fades out once at the bottom / not overflowing. */}
          <div
            className={cx(
              'pointer-events-none absolute inset-x-0 bottom-0 h-8 rounded-b-lg bg-gradient-to-t from-popover to-transparent transition-opacity duration-200',
              atBottom && 'opacity-0',
            )}
          />
        </div>

        <p className="text-sm text-muted-foreground">{t('closeProductConfirm.warning', { product: productName })}</p>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={onConfirm}>
            <Package />
            {t('closeProductConfirm.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloseProductConfirm;
